import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { i18n } from '../../translate/i18n';
import * as actions from '../../store/actions';
import { withRouter } from 'react-router-dom';

import {
  Container,
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Grid,
  CircularProgress,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  NavLinkStyled,
  LogoText,
  Appbar,
} from './styles';

import CheckCircle from "@mui/icons-material/CheckCircle";
import CloseIcon from '@mui/icons-material/Close';

import { isEmpty } from 'lodash';

import { roleNames } from '../../utils/roleNames';
import Spinner from '../../components/Spinner/index';
import { RedirectModal } from './RedirectModal';
import { toastr } from 'react-redux-toastr';

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },
  paper: {
    marginTop: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative'
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: "green",
    '&:hover': {
      backgroundColor: "green",
      opacity: .90,
    },
  },
}));

const ConfirmAddTenant = (props: any) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [role, setRole] = useState('doorman');


  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState<any>({});
  const [alreadyUsed, setAlreadyUsed] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [redirectModalOpen, setRedirectModalOpen] = useState(false);
  const [redirectModalType, setRedirectModalType] = useState(null);

  const getTokenConfirmAddTenantOrUser: any = useCallback(
    () => dispatch(actions.getTokenConfirmAddTenantOrUser(props.match.params.id)), []
  );

  const userToken = useSelector((state: any) => state.tokens.userToken);

  const inviteAddTenant: any = (body: any) => dispatch(actions.inviteAddTenant(body))

  const loadingUser = useSelector((state: any) => state.users.loading);
  const loadingToken = useSelector((state: any) => state.tokens.loading);


  useEffect(() => {
    getTokenConfirmAddTenantOrUser().then((response: any) => {
      if (response === 'error') {
        setAlreadyUsed(true);
      }
    })
  }, [])

  // in case editing, populate fields
  useEffect(() => {
    if (!isEmpty(userToken)) {
      setName(userToken.userId.name)
      setTenant(userToken.tenantDetails.tenant)
      setRole(userToken.tenantDetails.role)

    }
  }, [userToken])

  const handleSubmit = (e: any) => {
    e.preventDefault();
    let hasError = false;

    if (!password) {
      setPasswordError(true)
      hasError = true;
    }

    if (hasError) return;

    let body = {
      password,
      tokenId: props.match.params.id
    }

    inviteAddTenant(body).then((response: any) => {
      if (response === 'success') {
        setUserCreated(true);
      } else {
        if (response?.code === 'WRONG_PASSWORD') {
          return toastr.error(i18n.t('toastr.error'), i18n.t('toastr.wrongPassword'));
        } else {
          setRedirectModalType(response?.code || null);
          setRedirectModalOpen(true);
        }
      }
    })

  }

  const card = () => (
    <Box sx={styles.cardContainer}>
      {tenant.photo ? <Avatar src={tenant.photo.url} sx={styles.avatar} /> : <Avatar sx={styles.avatarSmallText}>{i18n.t('avatarConfirmTenant.logo') as string}</Avatar>}
      <Box sx={styles.cardBody}>
        <Box sx={styles.nameText}>{tenant.name}</Box>
        <Box sx={styles.roleText}>{roleNames(role)}</Box>
      </Box>
    </Box>
  )

  return (
    <>
      <Appbar />
      <Container component="main" maxWidth="xs" style={{ marginBottom: '100px' }}>
        <CssBaseline />
        {(!userCreated && !alreadyUsed) && (
          <Box className={classes.paper}>
            <LogoText />
            <form className={classes.form} noValidate onSubmit={handleSubmit}>
              {loadingToken && <Spinner overlay />}

              <Grid container spacing={2}>
                {i18n.t('gridLogin.text1') as string} {name}{i18n.t('gridLogin.text2') as string}
              {card()}
              </Grid>

              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={i18n.t('textFieldLogin.password') as string}
                type="password"
                id="password"
                onChange={event => setPassword(event.target.value)}
                value={password}
                error={passwordError}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={loadingUser}
                startIcon={loadingUser && <CircularProgress />}
              >
                {i18n.t('buttonConfirmTenant.text') as string}
          </Button>
            </form>
          </Box>)}
        {userCreated && (
          <Box className={classes.paper}>

            <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: "100%", height: "100%" }}>
              <CheckCircle style={{ height: "100px", width: "100px", color: "green" }} />
              <Box style={{ fontSize: "25px", fontWeight: 'bold', textAlign: "center" }}>{i18n.t('divConfirmTenant.access') as string}</Box>
              <NavLinkStyled variant="body2" to='/login'>{i18n.t('navLinkStyledConfirmTenant.text')}</NavLinkStyled>
            </Box>
          </Box>
        )}
        {alreadyUsed && (
          <Box className={classes.paper}>

            <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: "100%", height: "100%" }}>
              <CloseIcon style={{ height: "100px", width: "100px", color: "red" }} />
              <Box style={{ fontSize: "25px", fontWeight: 'bold', textAlign: "center" }}>{i18n.t('divConfirmTenant.link') as string}</Box>
              <NavLinkStyled variant="body2" to='/login'>{i18n.t('navLinkStyledConfirmTenant.text')}</NavLinkStyled>
            </Box>
          </Box>
        )}
        <RedirectModal 
          isModalOpen={redirectModalOpen} 
          closeDialog={() => setRedirectModalOpen(false)} 
          type={redirectModalType} 
        />
      </Container>
    </>
  );
}

export default withRouter(ConfirmAddTenant)

const styles = {
  cardContainer: {
    display: 'flex',
    flexDirection: 'row',
    border: "1px solid #ccc",
    padding: "10px 20px",
    borderRadius: "10px",
    width: "80%",
    maxWidth: "400px",
    margin: 5,
    position: "relative",
  },
  avatar: {
    width: 50,
    height: 50,
  },
  avatarSmallText: {
    fontSize: 12,
    width: 50,
    height: 50,
  },
  cardBody: {
    display: "flex",
    justifyContent: "center",
    paddingLeft: 10,
    flexDirection: 'column'
  },
  roleText: {
    fontSize: 12
  },
  nameText: {
    fontWeight: "bold"
  },
}
